import '../styles/globals.css';
import type { AppProps } from 'next/app';
import { IntlProvider, ReactIntlErrorCode } from 'react-intl';
import { useRouter } from 'next/router';
import ErrorBoundary from 'src/shared-components/ErrorBoundary/ErrorBoundary';
import { Locales } from 'src/utils/localization';
import GlobalHooksProvider from 'src/hooks/providers/GlobalHooksProvider';
import NProgress from 'nprogress';
import { useEffect, useRef } from 'react';
import useSessionConfigStore, {
  sessionConfigStoreFallback
} from 'src/hooks/useSessionConfigStore';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import useLocationStore from 'src/hooks/useLocationStore';
import useAuthorizationStore, {
  authorizationStoreFallback
} from 'src/hooks/useAuthorizationStore';
import { STORAGE_NAME_PREFIX, appCookies } from 'src/utils/cookieHelper';
import dynamic from 'next/dynamic';
import logger from 'src/utils/logger';
import {
  SSRMultyLevelCategoriesStoreContext,
  createSSRMultyLevelCategoriesStore
} from 'src/hooks/useSSRMultyLevelCategoriesStore';
import { HostStoreContext, createHostStore } from 'src/hooks/useHostStore';
import AnalyticsScriptsLoader from 'src/shared-components/AnalyticsScriptsLoader/AnalyticsScriptsLoader';
import useStoreDeep from 'src/hooks/useStore';
import { useReportWebVitals } from 'next/web-vitals';
import { Metric, postWebVitals } from 'src/api/requests/postWebVitals';
import isEmpty from 'lodash/isEmpty';
import {
  getEnvVariable,
  NEXT_PUBLIC_ENV,
  NEXT_PUBLIC_ENV_VARIANT
} from 'src/utils/envVariablesHelper';
import useUserLocation from 'src/hooks/useUserLocation';
import {
  ServerUserLocationStoreContext,
  createServerUserLocationStore
} from 'src/hooks/useServerUserLocationStore';
import CustomHead from './_head';

const CustomToastContainer = dynamic(
  () =>
    import('src/shared-components/CustomToastContainer/CustomToastContainer'),
  {
    ssr: false
  }
);

const FontsLoader = dynamic(() => import('src/styles/fonts/FontsLoader'), {
  ssr: false
});

function App({ Component, pageProps }: AppProps): JSX.Element {
  const router = useRouter();
  const { locale, defaultLocale } = router || {};
  const sessionConfigStore = useStoreDeep(useSessionConfigStore, (state) => ({
    setIsNewSession: state.setIsNewSession,
    isNewSession: state.isNewSession
  }));
  const { setIsNewSession, isNewSession } =
    sessionConfigStore ?? sessionConfigStoreFallback;

  const ssrMultyLevelCategoriesStore = useRef(
    createSSRMultyLevelCategoriesStore({
      multyLevelCategoriesList: pageProps.multyLevelCategoriesList
    })
  ).current;
  const serverUserLocationStore = useRef(
    createServerUserLocationStore({
      serverUserLocation: pageProps.serverUserLocation
    })
  ).current;

  const userLocation = useUserLocation();

  const hostUrlStore = useRef(
    createHostStore(
      {
        hostURL: pageProps.hostURL
      },
      locale,
      defaultLocale
    )
  ).current;
  const cities = useStoreDeep(useLocationStore, (state) => state.cities);
  const userToken = useStoreDeep(
    useAuthorizationStore,
    (state) => state.userToken
  );
  const isStoreInitialized = useStoreDeep(
    useAuthorizationStore,
    (state) => state.isStoreInitialized
  );
  const authStoreData = useStoreDeep(useAuthorizationStore, (state) => ({
    setIsStoreInitialized: state?.setIsStoreInitialized
  }));
  const { setIsStoreInitialized } = authStoreData ?? authorizationStoreFallback;

  const { trackCustomGtagEvent } = useGTAG();

  useEffect(() => {
    if (!isStoreInitialized) {
      setIsStoreInitialized(true);
    }
  }, [isStoreInitialized]);

  useEffect(() => {
    if (isStoreInitialized && cities?.length && isNewSession) {
      setIsNewSession(false);
      trackCustomGtagEvent(`${GTAGPrefix.likie}_start`, {
        city: userLocation?.name,
        login: userToken ? 'yes' : 'no'
      });
    }
  }, [isStoreInitialized, cities?.length, isNewSession]);

  useEffect(() => {
    NProgress.configure({ showSpinner: false });
    const handleStart = () => {
      NProgress.start();
    };

    const handleStop = () => {
      NProgress.done();
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleStop);
    router.events.on('routeChangeError', handleStop);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleStop);
      router.events.off('routeChangeError', handleStop);
    };
  }, [router]);

  useEffect(() => {
    // This hook expires app-related cookies with non-actual STORAGE_NAME_PREFIX versions
    if (isStoreInitialized) {
      const cookies = document.cookie.split(';');
      const actualCookieNames = Object.values(appCookies);
      actualCookieNames.forEach((cookieName) => {
        const existingOldCookie = cookies.find((cookie) => {
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
          return (
            // Do not wipe cookie if it includes actual prefix
            !name.includes(STORAGE_NAME_PREFIX) && name.includes(cookieName)
          );
        });
        if (existingOldCookie) {
          const eqPos = existingOldCookie.indexOf('=');
          const oldCookieName =
            eqPos > -1 ? existingOldCookie.substr(0, eqPos) : existingOldCookie;
          document.cookie = `${oldCookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        }
      });
    }
  }, [isStoreInitialized]);

  useReportWebVitals((metrics) => {
    try {
      const currentEnv = getEnvVariable(NEXT_PUBLIC_ENV);
      const isInternalMetricsEnabled = [
        NEXT_PUBLIC_ENV_VARIANT.PROD,
        NEXT_PUBLIC_ENV_VARIANT.STAGE
      ].includes(currentEnv);
      const firstSlug =
        router?.route.split('/')[1].replaceAll('[', '').replaceAll(']', '') ??
        'home';
      const metricsReport: Metric = {
        metricName: metrics?.name ?? '',
        metricValue: metrics?.value ?? '',
        metricRating: metrics?.rating ?? '',
        navigationType: metrics?.navigationType ?? '',
        pageType: isEmpty(firstSlug) ? 'home' : firstSlug,
        env: currentEnv.toUpperCase()
      };
      if (isInternalMetricsEnabled) {
        // Send web vitals to API
        setTimeout(() => {
          postWebVitals(metricsReport);
        });
      } else {
        // logger.log('Internal metrics report', metricsReport);
      }
    } catch (error) {
      logger.log('Error with useReportWebVitals', error);
    }
  });

  return (
    <>
      <FontsLoader />
      <IntlProvider
        locale={locale ?? Locales.UA}
        defaultLocale={defaultLocale ?? Locales.UA}
        messages={{}}
        onError={(err) => {
          if (err.code === ReactIntlErrorCode.MISSING_DATA) {
            // Ignore MISSING_DATA errors
            return;
          }
          logger.error(err.message);
        }}
      >
        <ErrorBoundary>
          <CustomHead />
          <HostStoreContext.Provider value={hostUrlStore}>
            <SSRMultyLevelCategoriesStoreContext.Provider
              value={ssrMultyLevelCategoriesStore}
            >
              <ServerUserLocationStoreContext.Provider
                value={serverUserLocationStore}
              >
                <Component {...pageProps} />
              </ServerUserLocationStoreContext.Provider>
            </SSRMultyLevelCategoriesStoreContext.Provider>
          </HostStoreContext.Provider>
          <GlobalHooksProvider />
        </ErrorBoundary>
      </IntlProvider>
      <CustomToastContainer />
      <div id="modal-root" />
      <AnalyticsScriptsLoader />
    </>
  );
}
export default App;
